<script lang="ts" setup>
import { computed } from 'vue'
import type { ChapterConfig } from '../configs/types'
import { getChapterConfig } from '../configs/brands'
import { getDailyPrice, getSavePercent, getUnitPriceValue, shouldShowDailyPrice, shouldShowPreferentialTotal } from '../utils/subscribeDailyPrice'
import vipDiscountIcon from '@/assets/vip_discount.png'
import vipIcon2 from '@/assets/vip_icon2.png'
import { SubscriptionCycle } from '@/utils/enum'
import { VIP_TYPE } from '@/constants'
import { useI18n } from '@/composables/useI18n'
import { brand, primaryColor } from '@/config/brands'

const props = defineProps<{
  item: any
  getUnitPrice: (item: any) => string | number
  isZero: (value: any) => boolean
  dark?: boolean
}>()

const { t } = useI18n()

// 加载当前品牌的 chapter 配置
const config: ChapterConfig = getChapterConfig()

const discountIcon = brand === 'talewave' ? '/images/talewave/vip/tw_ic_vip.png' : vipDiscountIcon

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '')
  if (!m)
    return `rgba(152, 117, 251, ${alpha})`
  return `rgba(${Number.parseInt(m[1], 16)}, ${Number.parseInt(m[2], 16)}, ${Number.parseInt(m[3], 16)}, ${alpha})`
}

// 折扣会员右上角周期文字：渐变 + 模糊，呈现柔和水印效果
// 夜间皮肤下品牌渐变末端会淡出至近白，深色底上突兀；改用主色淡出至低透明，水印更柔和
const cycleGradient = computed(() =>
  props.dark
    ? `linear-gradient(180deg, ${hexToRgba(primaryColor, 0.85)} 0%, ${hexToRgba(primaryColor, 0.25)} 100%)`
    : config.colors.purpleTextGradient,
)

// 角标背景：样式内聚在卡片组件内，保证所有引用场景（付费墙面板/弹窗）都能正确展示
const popularTagBg = config.colors.pinkGradient
</script>

<template>
  <div class="subscribe-item-root relative w-full h-full px-12px pt-20px pb-20px text-left text-n-text-1">
    <!-- 角标 -->
    <div v-if="item.cornerTip === 1 && item.marketingCopy" class="popular-tag">
      {{ item.marketingCopy }}
    </div>

    <!-- 折扣会员 -->
    <div v-if="item.subscribeType === VIP_TYPE.DISCOUNT" class="flex items-center">
      <!-- 折扣会员的周期背景,例如: Weekly -->
      <div class="cycleBg absolute font-bold top-0 right-0 text-32px leading-[30px]">
        {{ item.cycleStr }}
      </div>

      <img v-if="config.colors.showVipIcon" :src="discountIcon" class="w-26px! flex-shrink-0">
      <div class="flex-1 min-w-0 ml-10px">
        <div class="text-18px font-medium">
          {{ item.discountCoins }} Coins
        </div>
        <ul class="text-11px list-none">
          <li>
            <BasicFitText class="text-n-primary">
              {{ t('SET', { 0: item.discountOnceCoins }) }}
            </BasicFitText>
          </li>
          <li>
            <BasicFitText class="text-n-primary">
              {{ t('SET2', { 0: item.discountBonusCoins, 1: item.discountDailyCoins }) }}
            </BasicFitText>
          </li>
        </ul>
      </div>
      <div class="absolute right-12px top-1/2 -translate-y-1/2 text-right">
        <div class="text-18px font-bold whitespace-nowrap">
          $ {{ item.preferentialPrice || item.price }}
        </div>
        <div class="text-n-text-2 text-12px line-through whitespace-nowrap">
          $ {{ item.discountOriginalPrice }}
        </div>
      </div>
    </div>

    <!-- 畅读会员 -->
    <div v-else class="flex items-center relative h-full">
      <div v-if="config.colors.showVipIcon" class="flex-shrink-0">
        <img :src="vipIcon2" class="w-26px!">
      </div>

      <div class="flex-1 min-w-0 ml-8px flex flex-col justify-start">
        <div class="flex items-center gap-6px">
          <BasicFitText class="text-18px font-medium">
            {{ SubscriptionCycle.getItemByValue(item.cycle)?.label }}
          </BasicFitText>
          <div
            v-if="shouldShowDailyPrice(item, isZero)"
            class="flex-shrink-0 bg-[#F74984]/15 text-[#F74984] text-10px font-medium rounded-4px px-6px py-3px lh-8px"
          >
            SAVE {{ getSavePercent(item) }}%
          </div>
        </div>
        <!-- 覆写文案(ZKSN-251):一行放得下则单行展示,放不下按 \n 拆行并逐行缩放;接口文案保持截断兜底 -->
        <BasicFitLines
          v-if="item.subscribeTextOverridden"
          :text="item.subscribeText"
          class="text-n-primary text-10px"
        />
        <div v-else class="text-n-primary text-10px truncate-2-lines">
          {{ item.subscribeText }}
        </div>
      </div>

      <!-- 免费 -->
      <div v-if="isZero(item.preferentialPrice)">
        <div class="text-18px font-bold">
          {{ config.texts.payTemplateFree }}
        </div>
        <div class="text-n-text-2 text-12px line-through text-right">
          $ {{ item.price }}
        </div>
      </div>

      <!-- 无优惠价格：突出每日单价，无 SAVE 标签 -->
      <div v-else-if="!item.preferentialPrice" class="flex-shrink-0 text-right relative">
        <div class="text-18px font-bold whitespace-nowrap">
          <template v-if="getUnitPriceValue(item)">
            $ {{ getUnitPriceValue(item) }}<span class="text-12px font-normal text-n-text-2"> / Day</span>
          </template>
          <template v-else>
            $ {{ item.price }}
          </template>
        </div>

        <div v-if="getUnitPriceValue(item)" class="text-n-text-2 text-12px">
          $ {{ item.price }}
        </div>
      </div>

      <!-- 有优惠价格 -->
      <div v-else class="text-right">
        <div class="text-18px font-bold whitespace-nowrap">
          <template v-if="shouldShowDailyPrice(item, isZero)">
            $ {{ getDailyPrice(item) }}<span class="text-12px font-normal text-n-text-2"> / Day</span>
          </template>
          <template v-else>
            $ {{ item.preferentialPrice }}
          </template>
        </div>
        <div class="text-12px">
          <span class="text-n-text-2 line-through">$ {{ item.price }}</span>
          <span v-if="shouldShowPreferentialTotal(item, isZero)" class="text-n-text-1 font-medium ml-4px">$ {{ item.preferentialPrice }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 订阅角标：样式对齐订阅中心（SubscribePage）的 popular-tag。
   选择器带父级以提高优先级：各面板变体在全局命名空间写过同名规则，
   其中非当前渲染变体的 v-bind 变量无定义，会把 background 覆盖成无效值（角标白字透明底不可见的根因） */
.subscribe-item-root .popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  min-width: 56px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  box-sizing: border-box;
  background: v-bind(popularTagBg);
  border-radius: 8px 0px 12px 0px;
  font-weight: 400;
  font-size: 10px;
  color: #FFFFFF;
}

/* 折扣会员右上角周期：渐变文字（淡出至白）做成柔和水印，与 /subscribe 页一致 */
.cycleBg {
  background: v-bind(cycleGradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  pointer-events: none;
}

.truncate-2-lines {
  width: 100%;
  word-break: break-all;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}
</style>
